import React, { useState, useRef, useEffect } from 'react';
import { 
  Network, Search, Filter, Settings, Eye, EyeOff, 
  RotateCcw, ZoomIn, ZoomOut, Move, ArrowLeft, Brain
} from 'lucide-react';

const GraphViewPage = ({ solution, onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSettings, setFilterSettings] = useState({
    concepts: { visible: true, isolated: false },
    formulas: { visible: true, isolated: false },
    values: { visible: true, isolated: false },
    steps: { visible: true, isolated: false },
    relationships: { visible: true, isolated: false }
  });
  const [graphSettings, setGraphSettings] = useState({
    nodeSize: 1.2,
    linkDistance: 120,
    showLabels: true,
    showRelationshipLabels: true,
    animate: true,
    layout: 'force',
    edgeThickness: 1.5
  });

  const resetView = () => {
    // Reset graph view functionality
    console.log('Reset graph view');
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Solver"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Knowledge Graph Visualization</h1>
              <p className="text-sm text-gray-600">Interactive Mathematical Entity Network</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={resetView}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title="Reset View"
            >
              <RotateCcw className="w-4 h-4 text-gray-600" />
            </button>
            <button
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4 text-gray-600" />
            </button>
            <button
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Control Panel */}
        <div className="w-80 bg-white border-r border-gray-200 shadow-lg p-6 overflow-y-auto">
          {/* Search */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Search className="w-4 h-4" />
              Search & Filter
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search mathematical entities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Entity Filters */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Entity Types
            </h3>
            <div className="space-y-3">
              {Object.entries(filterSettings).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={value.visible}
                      onChange={(e) => setFilterSettings(prev => ({ 
                        ...prev, 
                        [key]: { ...prev[key], visible: e.target.checked }
                      }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                    <span className="ml-2 text-gray-700 font-medium capitalize">{key}</span>
                  </label>
                  <button
                    onClick={() => setFilterSettings(prev => ({ 
                      ...prev, 
                      [key]: { ...prev[key], isolated: !prev[key].isolated }
                    }))}
                    className={`p-1 rounded transition-colors ${
                      value.isolated ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                    }`}
                    title={`${value.isolated ? 'Show all' : 'Isolate'} ${key}`}
                  >
                    {value.isolated ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Graph Settings */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Display Settings
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 block mb-1">Node Size</label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={graphSettings.nodeSize}
                  onChange={(e) => setGraphSettings(prev => ({ ...prev, nodeSize: parseFloat(e.target.value) }))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{graphSettings.nodeSize.toFixed(1)}x</span>
              </div>
              
              <div>
                <label className="text-sm text-gray-600 block mb-1">Edge Thickness</label>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={graphSettings.edgeThickness}
                  onChange={(e) => setGraphSettings(prev => ({ ...prev, edgeThickness: parseFloat(e.target.value) }))}
                  className="w-full"
                />
                <span className="text-xs text-gray-500">{graphSettings.edgeThickness.toFixed(1)}x</span>
              </div>

              <div className="space-y-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={graphSettings.showLabels}
                    onChange={(e) => setGraphSettings(prev => ({ ...prev, showLabels: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  <span className="ml-2 text-gray-700">Show Labels</span>
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={graphSettings.animate}
                    onChange={(e) => setGraphSettings(prev => ({ ...prev, animate: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                  />
                  <span className="ml-2 text-gray-700">Enable Animations</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Main Graph Area */}
        <div className="flex-1 bg-white">
          {solution && solution.graphData ? (
            <div className="h-full bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
              <div className="h-full bg-white rounded-xl shadow-lg border border-gray-200 flex items-center justify-center">
                <div className="text-center">
                  <Network className="w-24 h-24 mx-auto mb-6 text-blue-400" />
                  <h3 className="text-2xl font-semibold text-gray-700 mb-3">Interactive Knowledge Graph</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed text-lg">
                    Mathematical entities and relationships visualization
                  </p>
                  <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                      <span>Click and drag to navigate</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      <span>Use zoom controls</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-200">
                  <Brain className="w-20 h-20 mx-auto mb-6 opacity-40 text-gray-400" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-3">No Graph Data Available</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    Solve a mathematical problem to generate the knowledge graph visualization.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>Return to solver to get started</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphViewPage; 